import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchProducts,
  addProductToStore,
  deleteProductFromStore,
} from "../redux/productAction";
import { uploadImageToCloudinary } from "../firebase/firebaseUtils";

/* ─── Styles ────────────────────────────────────────────────── */
const injectStyles = () => {
  if (document.getElementById("apm-styles")) return;
  const style = document.createElement("style");
  style.id = "apm-styles";
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');

    :root {
      --apm-bg: #0f0e0e;
      --apm-surface: #171615;
      --apm-card: #1e1c1b;
      --apm-border: rgba(255,255,255,0.07);
      --apm-gold: #c9a96e;
      --apm-gold-dim: rgba(201,169,110,0.15);
      --apm-rose: #d4788a;
      --apm-rose-dim: rgba(212,120,138,0.12);
      --apm-text: #f0ece6;
      --apm-muted: #7a736b;
      --apm-success: #6dbf8a;
      --apm-error: #e07070;
      --apm-radius: 12px;
      --apm-font-display: 'Cormorant Garamond', Georgia, serif;
      --apm-font-body: 'DM Sans', sans-serif;
    }

    * { box-sizing: border-box; }

    .apm-wrap {
      font-family: var(--apm-font-body);
      background: var(--apm-bg);
      min-height: 100vh;
      padding: 32px 20px;
      color: var(--apm-text);
    }

    /* ── Header ── */
    .apm-header {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      margin-bottom: 36px;
      padding-bottom: 20px;
      border-bottom: 1px solid var(--apm-border);
      flex-wrap: wrap;
      gap: 8px;
    }
    .apm-header-title {
      font-family: var(--apm-font-display);
      font-size: clamp(1.6rem, 5vw, 2.8rem);
      font-weight: 700;
      color: var(--apm-text);
      line-height: 1;
      letter-spacing: -0.02em;
      margin: 0;
    }
    .apm-header-title span { color: var(--apm-gold); }
    .apm-header-meta {
      font-size: 0.75rem;
      font-weight: 500;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: var(--apm-muted);
    }

    /* ── Layout: side-by-side on desktop, stacked on mobile ── */
    .apm-layout {
      display: grid;
      grid-template-columns: 360px 1fr;
      gap: 24px;
      align-items: start;
    }
    @media (max-width: 900px) {
      .apm-layout { grid-template-columns: 1fr; }
    }

    /* ── Panel (form) ── */
    .apm-panel {
      background: var(--apm-surface);
      border: 1px solid var(--apm-border);
      border-radius: var(--apm-radius);
      padding: 24px;
      position: sticky;
      top: 16px;
    }
    @media (max-width: 900px) {
      .apm-panel { position: static; }
    }
    .apm-panel-title {
      font-family: var(--apm-font-display);
      font-size: 1.3rem;
      font-weight: 600;
      color: var(--apm-gold);
      margin: 0 0 24px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .apm-panel-title::after {
      content: '';
      flex: 1;
      height: 1px;
      background: var(--apm-border);
    }

    /* ── Form fields ── */
    .apm-field { margin-bottom: 16px; }
    .apm-label {
      display: block;
      font-size: 0.7rem;
      font-weight: 600;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--apm-muted);
      margin-bottom: 7px;
    }
    .apm-input {
      width: 100%;
      background: var(--apm-card);
      border: 1px solid var(--apm-border);
      border-radius: 8px;
      color: var(--apm-text);
      font-family: var(--apm-font-body);
      font-size: 0.9rem;
      padding: 11px 14px;
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;
    }
    .apm-input:focus {
      border-color: var(--apm-gold);
      box-shadow: 0 0 0 3px var(--apm-gold-dim);
    }
    .apm-input::placeholder { color: var(--apm-muted); }
    .apm-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }

    /* ── Upload zone ── */
    .apm-upload {
      border: 1.5px dashed var(--apm-border);
      border-radius: 8px;
      padding: 20px;
      text-align: center;
      cursor: pointer;
      transition: border-color 0.2s, background 0.2s;
      background: var(--apm-card);
      position: relative;
    }
    .apm-upload:hover, .apm-upload.has-file {
      border-color: var(--apm-rose);
      background: var(--apm-rose-dim);
    }
    .apm-upload input {
      position: absolute;
      inset: 0;
      opacity: 0;
      cursor: pointer;
      width: 100%;
      height: 100%;
    }
    .apm-upload-icon { font-size: 1.4rem; margin-bottom: 6px; }
    .apm-upload-text { font-size: 0.8rem; color: var(--apm-muted); font-weight: 500; }
    .apm-upload-name { font-size: 0.75rem; color: var(--apm-rose); margin-top: 6px; font-style: italic; }

    /* ── Image preview ── */
    .apm-preview-wrap { margin-top: 10px; border-radius: 8px; overflow: hidden; }
    .apm-preview-img { width: 100%; max-height: 160px; object-fit: cover; display: block; border-radius: 8px; }

    /* ── Submit button ── */
    .apm-btn {
      width: 100%;
      padding: 13px;
      border: none;
      border-radius: 8px;
      font-family: var(--apm-font-body);
      font-size: 0.85rem;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      cursor: pointer;
      transition: opacity 0.2s, transform 0.15s;
      margin-top: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
    .apm-btn-primary {
      background: linear-gradient(135deg, var(--apm-gold), #b8894e);
      color: #0f0e0e;
    }
    .apm-btn:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
    .apm-btn:disabled { opacity: 0.45; cursor: not-allowed; }

    /* ── Success toast ── */
    .apm-toast {
      background: rgba(109,191,138,0.12);
      border: 1px solid rgba(109,191,138,0.3);
      color: var(--apm-success);
      border-radius: 8px;
      padding: 11px 14px;
      font-size: 0.82rem;
      margin-bottom: 14px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    /* ── Spinner ── */
    .apm-spinner {
      width: 16px; height: 16px;
      border: 2px solid rgba(0,0,0,0.2);
      border-top-color: #0f0e0e;
      border-radius: 50%;
      animation: apm-spin 0.7s linear infinite;
    }
    @keyframes apm-spin { to { transform: rotate(360deg); } }

    /* ── Product list panel ── */
    .apm-list-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 20px;
      flex-wrap: wrap;
      gap: 8px;
    }
    .apm-list-title {
      font-family: var(--apm-font-display);
      font-size: 1.4rem;
      font-weight: 600;
      color: var(--apm-text);
      margin: 0;
    }
    .apm-count-badge {
      background: var(--apm-gold-dim);
      color: var(--apm-gold);
      font-size: 0.7rem;
      font-weight: 600;
      letter-spacing: 0.08em;
      padding: 4px 10px;
      border-radius: 20px;
      border: 1px solid rgba(201,169,110,0.25);
    }

    /* ── Product grid — 2 cols on mobile, auto-fill on larger ── */
    .apm-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 14px;
    }
    @media (min-width: 500px) and (max-width: 900px) {
      .apm-grid { grid-template-columns: repeat(3, 1fr); }
    }
    @media (min-width: 900px) {
      .apm-grid { grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); }
    }

    /* ── Product card ── */
    .apm-card {
      background: var(--apm-surface);
      border: 1px solid var(--apm-border);
      border-radius: var(--apm-radius);
      overflow: hidden;
      transition: border-color 0.25s, transform 0.25s, box-shadow 0.25s;
    }
    .apm-card:hover {
      border-color: rgba(201,169,110,0.3);
      transform: translateY(-3px);
      box-shadow: 0 12px 32px rgba(0,0,0,0.4);
    }
    .apm-card-img { width: 100%; height: 150px; object-fit: cover; display: block; }
    @media (max-width: 480px) {
      .apm-card-img { height: 120px; }
    }
    .apm-card-img-placeholder {
      width: 100%;
      height: 150px;
      background: var(--apm-card);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 2rem;
      color: var(--apm-muted);
    }
    .apm-card-body { padding: 12px; }
    .apm-card-name {
      font-family: var(--apm-font-display);
      font-size: 1rem;
      font-weight: 600;
      color: var(--apm-text);
      margin: 0 0 4px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .apm-card-price {
      font-size: 0.85rem;
      font-weight: 600;
      color: var(--apm-gold);
      margin: 0 0 3px;
    }
    .apm-card-qty { font-size: 0.72rem; color: var(--apm-muted); margin: 0 0 10px; }
    .apm-card-footer { display: flex; align-items: center; justify-content: space-between; }
    .apm-status {
      font-size: 0.65rem;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      padding: 3px 8px;
      border-radius: 20px;
    }
    .apm-status-in {
      background: rgba(109,191,138,0.12);
      color: var(--apm-success);
      border: 1px solid rgba(109,191,138,0.25);
    }
    .apm-status-out {
      background: rgba(224,112,112,0.12);
      color: var(--apm-error);
      border: 1px solid rgba(224,112,112,0.25);
    }
    .apm-delete-btn {
      background: none;
      border: none;
      cursor: pointer;
      padding: 6px;
      border-radius: 6px;
      color: var(--apm-muted);
      transition: color 0.2s, background 0.2s;
      display: flex;
      align-items: center;
    }
    .apm-delete-btn:hover { color: var(--apm-error); background: rgba(224,112,112,0.1); }

    /* ── Empty / loading ── */
    .apm-empty {
      text-align: center;
      padding: 50px 20px;
      color: var(--apm-muted);
      grid-column: 1 / -1;
    }
    .apm-empty-icon { font-size: 2.2rem; margin-bottom: 10px; }
    .apm-empty-text { font-size: 0.88rem; }
    .apm-error-banner {
      background: rgba(224,112,112,0.1);
      border: 1px solid rgba(224,112,112,0.25);
      color: var(--apm-error);
      border-radius: 8px;
      padding: 12px 16px;
      font-size: 0.85rem;
      margin-bottom: 24px;
    }
    .apm-loading-wrap { display: flex; justify-content: center; padding: 50px 0; }
    .apm-big-spinner {
      width: 30px; height: 30px;
      border: 2.5px solid var(--apm-border);
      border-top-color: var(--apm-gold);
      border-radius: 50%;
      animation: apm-spin 0.8s linear infinite;
    }
  `;
  document.head.appendChild(style);
};

/* ─── Trash icon ─────────────────────────────────────────────── */
function TrashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
      <path d="M10 11v6M14 11v6"/>
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  );
}

/* ─── Main component ─────────────────────────────────────────── */
const AdminProductManagement = () => {
  const dispatch = useDispatch();
  const { products, loading, error } = useSelector((s) => s.product);

  const [form, setForm] = useState({
    name: "", price: "", quantity: "", soldOut: false, image: null,
  });
  const [preview, setPreview]     = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast]         = useState(false);

  useEffect(() => { injectStyles(); }, []);
  useEffect(() => { dispatch(fetchProducts()); }, [dispatch]);

  /* Clean up preview URL on unmount */
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleImageChange = (e) => {
    const file = e.target.files[0] || null;
    setForm((f) => ({ ...f, image: file }));
    if (file) {
      if (preview) URL.revokeObjectURL(preview);
      setPreview(URL.createObjectURL(file));
    }
  };

  const showToast = () => {
    setToast(true);
    setTimeout(() => setToast(false), 3000);
  };

  const handleAdd = async () => {
    if (!form.name || !form.price || !form.quantity) {
      alert("Please fill in name, price, and quantity.");
      return;
    }
    if (!form.image) {
      alert("Please upload a product image.");
      return;
    }
    setSubmitting(true);
    try {
      const imageUrl = await uploadImageToCloudinary(form.image);

      /* ── FIX: await the dispatch so Firestore write completes
              before we reset the form. Your addProductToStore
              action must return a Promise (thunk).            ── */
      await dispatch(addProductToStore({
        name:     form.name,
        price:    parseFloat(form.price),
        quantity: parseInt(form.quantity, 10),
        soldOut:  form.soldOut,
        imageUrl,
      }));

      /* Re-fetch so the shop page (and this list) reflects the
         new document that now exists in Firestore.            */
      dispatch(fetchProducts());

      setForm({ name: "", price: "", quantity: "", soldOut: false, image: null });
      setPreview(null);
      showToast();
    } catch (err) {
      console.error("Error adding product:", err);
      alert("Failed to add product. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm("Remove this product?")) {
      dispatch(deleteProductFromStore(id));
    }
  };

  const today = new Date().toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  });

  return (
    <div className="apm-wrap">

      {/* Header */}
      <div className="apm-header">
        <h1 className="apm-header-title">Product <span>Studio</span></h1>
        <span className="apm-header-meta">{today}</span>
      </div>

      {error && <div className="apm-error-banner">⚠ {error}</div>}

      <div className="apm-layout">

        {/* ── Add Product Form ── */}
        <div className="apm-panel">
          <h2 className="apm-panel-title">New Product</h2>

          {toast && (
            <div className="apm-toast">
              ✓ Product added successfully
            </div>
          )}

          <div className="apm-field">
            <label className="apm-label">Product Name</label>
            <input
              className="apm-input"
              placeholder="e.g. Silky Closure Wig"
              value={form.name}
              onChange={set("name")}
            />
          </div>

          <div className="apm-row">
            <div className="apm-field">
              <label className="apm-label">Price (GH₵)</label>
              <input
                className="apm-input"
                type="number"
                placeholder="0.00"
                value={form.price}
                onChange={set("price")}
                min="0"
              />
            </div>
            <div className="apm-field">
              <label className="apm-label">Quantity</label>
              <input
                className="apm-input"
                type="number"
                placeholder="0"
                value={form.quantity}
                onChange={set("quantity")}
                min="0"
              />
            </div>
          </div>

          <div className="apm-field">
            <label className="apm-label">Product Image</label>
            <div className={`apm-upload ${form.image ? "has-file" : ""}`}>
              <input type="file" accept="image/*" onChange={handleImageChange} />
              <div className="apm-upload-icon">{form.image ? "✓" : "↑"}</div>
              <div className="apm-upload-text">
                {form.image ? "Image selected" : "Click to upload"}
              </div>
              {form.image && (
                <div className="apm-upload-name">{form.image.name}</div>
              )}
            </div>
            {preview && (
              <div className="apm-preview-wrap">
                <img src={preview} alt="Preview" className="apm-preview-img" />
              </div>
            )}
          </div>

          <button
            className="apm-btn apm-btn-primary"
            onClick={handleAdd}
            disabled={submitting || loading}
          >
            {submitting
              ? <><div className="apm-spinner" /> Adding…</>
              : "Add Product"
            }
          </button>
        </div>

        {/* ── Product List ── */}
        <div>
          <div className="apm-list-header">
            <h2 className="apm-list-title">Inventory</h2>
            <span className="apm-count-badge">
              {products.length} {products.length === 1 ? "item" : "items"}
            </span>
          </div>

          {loading ? (
            <div className="apm-loading-wrap">
              <div className="apm-big-spinner" />
            </div>
          ) : (
            <div className="apm-grid">
              {products.length === 0 ? (
                <div className="apm-empty">
                  <div className="apm-empty-icon">🧴</div>
                  <div className="apm-empty-text">No products yet. Add one to get started.</div>
                </div>
              ) : (
                products.map((p) => (
                  <div className="apm-card" key={p.id}>
                    {p.imageUrl
                      ? <img className="apm-card-img" src={p.imageUrl} alt={p.name} />
                      : <div className="apm-card-img-placeholder">🖼</div>
                    }
                    <div className="apm-card-body">
                      <p className="apm-card-name" title={p.name}>{p.name}</p>
                      <p className="apm-card-price">GH₵ {Number(p.price).toFixed(2)}</p>
                      <p className="apm-card-qty">Qty: {p.quantity}</p>
                      <div className="apm-card-footer">
                        <span className={`apm-status ${p.soldOut ? "apm-status-out" : "apm-status-in"}`}>
                          {p.soldOut ? "Sold Out" : "In Stock"}
                        </span>
                        <button
                          className="apm-delete-btn"
                          onClick={() => handleDelete(p.id)}
                          aria-label={`Delete ${p.name}`}
                          title="Delete product"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default AdminProductManagement;